import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogIn, LayoutDashboard, Clock, Monitor, Activity, PlusCircle, Edit, Trash2 } from 'lucide-react';
import api from '../../services/api';
import i18n from '../../i18n/config';

function formatDate(iso) {
    if (!iso) return '—';
    const utcStr = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
    const d = new Date(utcStr);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}-${mm}-${yyyy}, ${hh}:${min}`;
}

function formatDuration(secs, t) {
    if (!secs) return '—';
    if (secs < 60) return `${secs} ${t('history.sec')}`;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    if (m < 60) return `${m} ${t('history.min')} ${s > 0 ? s + ' ' + t('history.sec') : ''}`.trim();
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h} ${t('history.hour')} ${rm > 0 ? rm + ' ' + t('history.min') : ''}`.trim();
}

export default function GlobalHistoryPage() {
    const { t } = useTranslation();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [action, setAction] = useState('');

    const ACTION_TABS = [
        { key: '', label: t('history.tab_all') },
        { key: 'login', label: t('history.tab_logins'), icon: LogIn },
        { key: 'view_dashboard', label: t('history.tab_dashboard'), icon: LayoutDashboard },
        { key: 'create', label: t('history.tab_create'), icon: PlusCircle },
        { key: 'update', label: t('history.tab_update'), icon: Edit },
        { key: 'delete', label: t('history.tab_delete'), icon: Trash2 },
    ];

    useEffect(() => {
        setLoading(true);
        const backendAction = ['login', 'view_dashboard'].includes(action) ? action : '';
        const params = backendAction ? `?action=${backendAction}&limit=500` : '?limit=500';
        api.get(`/admin/activity${params}`)
            .then(r => setActivities(r.data))
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [action]);

    const filteredActivities = activities.filter(a => {
        if (!action) return true;
        if (action === 'create') return String(a.action).startsWith('create_');
        if (action === 'update') return String(a.action).startsWith('update_');
        if (action === 'delete') return String(a.action).startsWith('delete_');
        return action === 'view_dashboard' || action === 'login' ? true : a.action === action; 
        // For login and view_dashboard, backend already filtered, so return true.
    });

    // Stats
    const totalLogins = activities.filter(a => a.action === 'login').length;
    const totalDashViews = activities.filter(a => a.action === 'view_dashboard').length;
    const totalSeconds = activities.reduce((acc, a) => acc + (a.duration_seconds || 0), 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <div className="p-2 rounded-lg bg-surface border border-border text-brand">
                    <Activity className="w-6 h-6" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-primary">{t('history.global_title')}</h2>
                    <p className="text-sm text-muted">{t('history.global_subtitle')}</p>
                </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { icon: LogIn, label: t('history.total_logins'), value: totalLogins, color: 'text-brand' },
                    { icon: LayoutDashboard, label: t('history.dashboard_views'), value: totalDashViews, color: 'text-success' },
                    { icon: Clock, label: t('history.total_time'), value: formatDuration(totalSeconds, t), color: 'text-warning' },
                ].map(({ icon: Icon, label, value, color }) => (
                    <div key={label} className="bg-surface border border-border rounded-2xl p-5 flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-surface-2 ${color}`}>
                            <Icon className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-xs text-muted font-semibold uppercase tracking-wider">{label}</p>
                            <p className={`text-2xl font-bold ${color}`}>{value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter tabs */}
            <div className="flex gap-2">
                {ACTION_TABS.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setAction(tab.key)}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors
                            ${action === tab.key
                                ? 'bg-brand text-inverse shadow-brand-sm'
                                : 'bg-surface border border-border text-secondary hover:text-primary hover:border-brand/40'
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Table */}
            <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
                <table className="w-full text-sm text-left">
                    <thead className="bg-surface-2 border-b border-border text-muted text-xs uppercase">
                        <tr>
                            <th className="px-6 py-4 font-semibold">{t('history.col_user')}</th>
                            <th className="px-6 py-4 font-semibold">{t('history.col_action')}</th>
                            <th className="px-6 py-4 font-semibold">{t('history.col_dashboard')}</th>
                            <th className="px-6 py-4 font-semibold">{t('history.col_duration')}</th>
                            <th className="px-6 py-4 font-semibold">{t('history.col_ip')}</th>
                            <th className="px-6 py-4 font-semibold">{t('history.col_date')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {loading ? (
                            <tr><td colSpan={6} className="px-6 py-10 text-center text-muted">{t('history.loading')}</td></tr>
                        ) : error ? (
                            <tr><td colSpan={6} className="px-6 py-10 text-center text-error">{error}</td></tr>
                        ) : filteredActivities.length === 0 ? (
                            <tr><td colSpan={6} className="px-6 py-10 text-center text-muted">{t('history.not_found')}</td></tr>
                        ) : filteredActivities.map(a => (
                            <tr key={a.id} className="hover:bg-surface-2/50 transition-colors">
                                <td className="px-6 py-4 text-primary font-bold">
                                    {(i18n.language === 'en' ? (a.user_name_en || a.user_name_uz || a.user_name) : (i18n.language === 'uz' ? (a.user_name_uz || a.user_name) : a.user_name)) || t('history.unknown_user')}
                                </td>
                                <td className="px-6 py-4">
                                        {a.action === 'login' && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand/10 text-brand text-xs font-bold rounded-lg">
                                            <LogIn className="w-3 h-3" /> {t('history.action_login')}
                                        </span>
                                    )}
                                    {a.action === 'view_dashboard' && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-success/10 text-success text-xs font-bold rounded-lg">
                                            <Monitor className="w-3 h-3" /> {t('history.action_dashboard')}
                                        </span>
                                    )}
                                    {String(a.action).startsWith('create_') && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/10 text-blue-500 text-xs font-bold rounded-lg">
                                            <PlusCircle className="w-3 h-3" /> {t('history.action_create')}
                                        </span>
                                    )}
                                    {String(a.action).startsWith('update_') && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 text-amber-500 text-xs font-bold rounded-lg">
                                            <Edit className="w-3 h-3" /> {t('history.action_update')}
                                        </span>
                                    )}
                                    {String(a.action).startsWith('delete_') && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-500/10 text-red-500 text-xs font-bold rounded-lg">
                                            <Trash2 className="w-3 h-3" /> {t('history.action_delete')}
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-primary font-medium">
                                    {a.subcategory_name || a.details || '—'}
                                </td>
                                <td className="px-6 py-4 text-muted">
                                    {a.duration_seconds != null ? formatDuration(a.duration_seconds, t) : '—'}
                                </td>
                                <td className="px-6 py-4 font-mono text-xs text-muted">
                                    {a.ip_address || '—'}
                                </td>
                                <td className="px-6 py-4 text-muted whitespace-nowrap">
                                    {formatDate(a.created_at)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
